import { useState } from 'react';

export default function CockpitSection() {
  const [activeTab, setActiveTab] = useState<'vfr' | 'ifr'>('vfr');

  const vfrChecklist = [
    { id: 1, item: 'ATIS Information', status: 'complete', detail: 'KPAO Info Charlie' },
    { id: 2, item: 'Taxi Clearance', status: 'complete', detail: 'Taxi via Alpha to 31' },
    { id: 3, item: 'Takeoff Clearance', status: 'pending', detail: 'Awaiting clearance' },
    { id: 4, item: 'Departure Frequency', status: 'pending', detail: '120.9' },
  ];

  const ifrChecklist = [
    { id: 1, item: 'Clearance Delivery', status: 'complete', detail: 'As filed, expect 5000' },
    { id: 2, item: 'Taxi Clearance', status: 'complete', detail: 'Taxi via Bravo to 13R' },
    { id: 3, item: 'Takeoff Clearance', status: 'pending', detail: 'Hold short 13R' },
    { id: 4, item: 'Departure Frequency', status: 'pending', detail: '125.35' },
    { id: 5, item: 'Squawk Code', status: 'complete', detail: '4521' },
  ];

  const activeChecklist = activeTab === 'vfr' ? vfrChecklist : ifrChecklist;

  return (
    <section className="py-16 sm:py-24 bg-[#0A1628]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            Your AI copilot in the cockpit.
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            WingmanFix listens to ATC, transcribes every call, and keeps a live checklist so you never miss a clearance or forget a frequency.
          </p>
        </div>

        {/* Split Screen Demo */}
        <div className="max-w-6xl mx-auto bg-gradient-to-br from-[#0D1B2E] to-[#050A14] rounded-3xl overflow-hidden border border-[#00FFD1]/20 shadow-2xl">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Left: Live Transcript */}
            <div className="bg-[#08121F] p-5 sm:p-8 border-b lg:border-b-0 lg:border-r border-gray-700">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-white uppercase tracking-wider">Live Radio Transcript</h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse"></div>
                  <span className="text-xs sm:text-sm text-[#00FFD1]">Recording</span>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {[
                  { speaker: 'ATC', text: 'Skyhawk 234AB, Palo Alto Tower, runway 31, cleared for takeoff.', time: '14:23:18' },
                  { speaker: 'You', text: 'Cleared for takeoff runway 31, Skyhawk 234AB.', time: '14:23:22' },
                  { speaker: 'ATC', text: 'Skyhawk 234AB, contact departure on 120.9.', time: '14:24:05' },
                ].map((msg, idx) => (
                  <div key={idx} className={`p-3 sm:p-4 rounded-xl border ${msg.speaker === 'ATC' ? 'bg-[#0D1B2E] border-[#00A3E0]/30' : 'bg-[#0F1F35] border-[#B4FF39]/30'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs sm:text-sm font-semibold uppercase tracking-wider ${msg.speaker === 'ATC' ? 'text-[#00A3E0]' : 'text-[#B4FF39]'}`}>
                        {msg.speaker}
                      </span>
                      <span className="text-xs text-gray-600 font-mono">{msg.time}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">{msg.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 sm:mt-6 flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                <i className="ri-information-line"></i>
                <span>All radio calls are automatically transcribed and saved</span>
              </div>
            </div>

            {/* Right: Smart Checklist */}
            <div className="bg-[#0A1628] p-5 sm:p-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-white uppercase tracking-wider">Smart Checklist</h3>
                <div className="flex gap-1 bg-[#050A14] p-1 rounded-lg border border-gray-700">
                  <button
                    onClick={() => setActiveTab('vfr')}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === 'vfr' ? 'bg-[#00FFD1] text-black' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    VFR
                  </button>
                  <button
                    onClick={() => setActiveTab('ifr')}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === 'ifr' ? 'bg-[#00FFD1] text-black' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    IFR
                  </button>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                {activeChecklist.map((item) => (
                  <div
                    key={item.id}
                    className={`p-3 sm:p-4 rounded-xl border transition-all ${
                      item.status === 'complete'
                        ? 'bg-[#00FFD1]/5 border-[#00FFD1]/30'
                        : 'bg-[#0D1B2E] border-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
                        item.status === 'complete' ? 'bg-[#00FFD1]' : 'bg-gray-700'
                      }`}>
                        {item.status === 'complete' ? (
                          <i className="ri-check-line text-black text-xs sm:text-sm"></i>
                        ) : (
                          <i className="ri-time-line text-gray-400 text-xs sm:text-sm"></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs sm:text-sm font-semibold mb-1 ${
                          item.status === 'complete' ? 'text-[#00FFD1]' : 'text-white'
                        }`}>
                          {item.item}
                        </p>
                        <p className="text-xs text-gray-500">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-[#050A14] rounded-xl border border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#B4FF39]/10 rounded-lg flex-shrink-0">
                    <i className="ri-lightbulb-line text-base sm:text-lg text-[#B4FF39]"></i>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                    WingmanFix automatically checks off items as you complete them
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mt-16 sm:mt-24 lg:mt-32">
          {[
            {
              icon: 'ri-mic-line',
              title: 'Always Listening',
              desc: 'Captures every ATC call automatically',
              color: '#00FFD1',
            },
            {
              icon: 'ri-file-text-line',
              title: 'Instant Transcription',
              desc: 'See your radio calls in real-time text',
              color: '#00A3E0',
            },
            {
              icon: 'ri-checkbox-circle-line',
              title: 'Smart Checklists',
              desc: 'Auto-updates based on your clearances',
              color: '#B4FF39',
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="bg-[#0D1B2E] rounded-2xl p-5 sm:p-6 lg:p-8 border border-gray-700 hover:border-[#00FFD1]/40 transition-all"
            >
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl mb-3 sm:mb-4"
                style={{ backgroundColor: `${feature.color}15` }}
              >
                <i className={`${feature.icon} text-xl sm:text-2xl`} style={{ color: feature.color }}></i>
              </div>
              <h4 className="text-base sm:text-lg font-semibold text-white mb-2">{feature.title}</h4>
              <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}